import React, { useEffect, useRef, useState } from 'react';
import FullCalendar, {
	EventApi,
	EventClickArg,
	EventContentArg,
} from '@fullcalendar/react';
import resourceTimelinePlugin from '@fullcalendar/resource-timeline';
import interactionPlugin from '@fullcalendar/interaction';
import listPlugin from '@fullcalendar/list';
import { useAppContext } from '../../../../Services/AppContext';
import { Moment } from 'moment';
import { getMomentDate } from '../../../../Utils/dates.util';
import useTranslation from '../../../../Hooks/useTranslation';
import styles from './TimelineCalendar.module.scss';
import { CalendarView } from '../../Enums/CalendarView.eunm';
import { CalendarViewIntervalEnum } from '../../Enums/CalendarViewInterval.enum';
import { CustomSettingKey } from '../../../Base/Enums/CustomSettingKey.enum';
import { useEffectAsync } from '../../../../helpers';
import useLoading from '../../../Base/Hooks/useLoading';
import useCustomSettings from '../../../Base/Hooks/useCustomSettings';
import { formatHour } from '../../Helpers/formatSlotHour.helper';
import CalendarSettingsModal from '../CalendarSettings/CalendarSettingsModal';
import { TIMELINE_CALENDAR_SLOT_DURATION_OPTIONS } from '../../Consts/TimelineCalendarSlotDurationOptions.const';
import { ResourceLabelMountArg } from '@fullcalendar/resource-common';
import moment from 'moment';

export type TimelineCalendarOnDateClick = (
	start: Moment,
	end: Moment,
	resourceId: string,
	resourceTitle?: string,
) => void;

export type TimelineCustomButtons = Record<
	string,
	TimelineCalendarCustomButton
>;

export interface TimelineCalendarCustomButton {
	text: string;
	click: () => void;
}

export interface TimelineCalendarResource {
	id: string;
	title: string;
	parentId?: string;
	groupKey?: string;
	isSelectionDisabled?: boolean;
	order?: number;
}

export interface TimelineCalendarEvent {
	id: string;
	resourceId: string;
	start: string;
	end: string;
	title: string;
	backgroundColor?: string;
	borderColor?: string;
	textColor?: string;
	extendedProps?: Record<string, any>;
}

interface Props {
	resources: TimelineCalendarResource[];
	events: TimelineCalendarEvent[];
	onDateRangeChange: (start: Moment, end: Moment) => void;
	onDateClick: TimelineCalendarOnDateClick;
	onEventClick: (data: EventClickArg) => void;
	onResourceClick?: (data: ResourceLabelMountArg) => void;
	onViewChange?: (view: CalendarView) => void;
	renderEvent?: (view: EventContentArg) => void;
	resourcesLabel?: string;
	onPrint?: () => void;
	customButtons?: {
		left?: TimelineCustomButtons;
		right?: TimelineCustomButtons;
	};
	resourceGroupField?: string;
	customSettingKey: CustomSettingKey;
	workplaceId: string;
}

export interface TimelineCalendarSettings {
	slotMinTime?: string;
	slotMaxTime?: string;
	slotDurationDay?: CalendarViewIntervalEnum;
	slotDurationWeek?: CalendarViewIntervalEnum;
}

const TimelineCalendar: React.FC<Props> = ({
	resources,
	events,
	onDateRangeChange,
	onDateClick,
	onEventClick,
	onResourceClick,
	onViewChange,
	resourcesLabel,
	renderEvent,
	onPrint,
	customButtons: customButtonsProps,
	resourceGroupField,
	customSettingKey,
	workplaceId,
}) => {
	const [isCalendarViewSettingsOpen, setIsCalendarViewSettingsOpen] =
		useState(false);
	const [isCalendarViewSettingsAvailable, setIsCalendarViewSettingsAvailable] =
		useState(false);
	const [isPrintAvailable, setIsPrintAvailable] = useState(false);
	const calendarRef = useRef<FullCalendar | null>(null);
	const [slotDuration, setSlotDuration] = useState<string | undefined>(
		CalendarViewIntervalEnum.THIRTY_MINUTES,
	);
	const [settings, setSettings] = useState<
		TimelineCalendarSettings | undefined
	>();
	const { language } = useAppContext();
	const { t } = useTranslation();
	const { isLoading, setIsLoading, RenderLoading } = useLoading(true);
	const { userData } = useAppContext();
	const { getCustomSettings } = useCustomSettings(workplaceId, userData.id);

	const calendarApi = calendarRef?.current?.getApi();

	const removeNativeTooltips = () => {
		const buttons = document.querySelectorAll('button[title]');
		buttons.forEach((btn: any) => btn.removeAttribute('title'));
	};

	const handleDatesSet = (dateInfo: { startStr: string; endStr: string }) => {
		setTimeout(removeNativeTooltips, 0);
		onDateRangeChange(
			getMomentDate(dateInfo.startStr),
			getMomentDate(dateInfo.endStr),
		);
	};

	const handleOnDateClick = (data: {
		startStr: string;
		endStr: string;
		resource?: {
			id: string;
			title: string;
		};
	}) => {
		if (data.resource) {
			onDateClick(
				getMomentDate(data.startStr),
				getMomentDate(data.endStr),
				data.resource.id,
				data.resource.title,
			);
		}
	};

	const handleOnEventClick = (data: EventClickArg) => {
		onEventClick(data);
	};

	useEffect(() => {
		if (calendarApi?.view.type) {
			onViewChange?.(calendarApi.view.type as CalendarView);
			let printAvailable = false;
			switch (calendarApi.view.type) {
				case CalendarView.TIMELINE_WEEK:
					setIsCalendarViewSettingsAvailable(true);
					setSlotDuration(
						settings?.slotDurationWeek ?? CalendarViewIntervalEnum.SIX_HOURS,
					);
					break;
				case CalendarView.TIMELINE_MOTH:
					setIsCalendarViewSettingsAvailable(false);
					setSlotDuration(undefined);
					break;
				case CalendarView.LIST_WEEK:
					printAvailable = true;
					setIsCalendarViewSettingsAvailable(false);
					break;
				case CalendarView.TIMELINE_DAY:
				default:
					setIsCalendarViewSettingsAvailable(true);
					setSlotDuration(
						settings?.slotDurationDay ??
							CalendarViewIntervalEnum.THIRTY_MINUTES,
					);
					break;
			}
			setIsPrintAvailable(printAvailable);
		}
	}, [calendarApi?.view, settings]);

	useEffect(() => {
		setTimeout(removeNativeTooltips, 0);
	}, []);

	const loadCalendarCustomSettings = async () => {
		setIsLoading(true);
		const settings = await getCustomSettings(customSettingKey);
		if (settings) {
			setSettings({
				slotMinTime: formatHour(settings.calendarViewTime?.showCalendarFrom),
				slotMaxTime: formatHour(settings.calendarViewTime?.showCalendarTo),
				slotDurationDay: settings.defaultSlotDurationDay?.value,
				slotDurationWeek: settings.defaultSlotDurationWeek?.value,
			});
		}
		setIsLoading(false);
	};

	useEffectAsync(async () => {
		await loadCalendarCustomSettings();
	}, []);

	const onSaveTimelineCalendarViewSettings = async () => {
		setIsCalendarViewSettingsOpen(false);
		await loadCalendarCustomSettings();
	};

	const getTextWidth = (text: string, font: string = '14px Arial'): number => {
		const canvas = document.createElement('canvas');
		const context = canvas.getContext('2d');
		if (!context) return 0;
		context.font = font;
		return context.measureText(text).width;
	};

	const getMaxResourceNameWidth = (
		resources: TimelineCalendarResource[],
		font?: string,
	): number => {
		const max = Math.max(...resources.map(r => getTextWidth(r.title, font)));
		return isFinite(max) ? Math.ceil(max + 70) : 200;
	};

	const resourceAreaWidth = getMaxResourceNameWidth(resources) + 'px';

	let headerToolBarRight =
		'resourceTimelineDay,resourceTimelineWeek,resourceTimelineMonth,listWeek';
	let headerToolBarLeft = 'prev,next,today';

	if (isPrintAvailable && onPrint) {
		headerToolBarRight += ',print';
	}

	if (isCalendarViewSettingsAvailable) {
		headerToolBarLeft += ',calendarViewSettings';
	}

	const customButtons: TimelineCustomButtons = {
		calendarViewSettings: {
			text: t('component.calendarViewSettings.modalTitle'),
			click: () => setIsCalendarViewSettingsOpen(true),
		},
		...customButtonsProps?.left,
		...customButtonsProps?.right,
	};

	if (onPrint) {
		customButtons.print = {
			text: t('component.timelineCalendar.print'),
			click: () => onPrint?.(),
		};
	}

	headerToolBarLeft = [
		headerToolBarLeft,
		...Object.keys(customButtonsProps?.left ?? {}),
	].join(',');

	headerToolBarRight = [
		headerToolBarRight,
		...Object.keys(customButtonsProps?.right ?? {}),
	].join(',');

	const eventOrder = (a: unknown, b: unknown): number => {
		const eventA = a as EventApi | undefined;
		const eventB = b as EventApi | undefined;

		if (!eventA || !eventB) {
			return 0;
		}

		const orderA: number = eventA?.extendedProps?.order ?? 0;
		const orderB: number = eventB?.extendedProps?.order ?? 0;

		if (orderA !== orderB) {
			return orderA - orderB;
		}

		// FALLBACK
		const startA = moment(eventA.start);
		const startB = moment(eventB.start);

		return startA.valueOf() - startB.valueOf();
	};

	if (isLoading) {
		return <RenderLoading />;
	}

	return (
		<div className='fullCalendarTimeline'>
			<FullCalendar
				ref={calendarRef}
				schedulerLicenseKey={'0784609858-fcs-1664878357'}
				plugins={[resourceTimelinePlugin, interactionPlugin, listPlugin]}
				initialView='resourceTimelineWeek'
				slotDuration={slotDuration}
				aspectRatio={1.5}
				headerToolbar={{
					left: headerToolBarLeft,
					center: 'title',
					right: headerToolBarRight,
				}}
				locale={language}
				noEventsText={t('base.noEventsToDisplay')}
				resources={resources}
				resourceGroupField={resourceGroupField}
				resourceOrder='order,title'
				events={events}
				nowIndicator={true}
				selectable={true}
				longPressDelay={100}
				selectMirror={true}
				buttonText={{
					today: t('base.shared.now'),
					month: t('base.months.one'),
					week: t('base.weeks.one'),
					day: t('base.days.one'),
					list: t('base.shared.list'),
				}}
				firstDay={1}
				eventClick={handleOnEventClick}
				datesSet={handleDatesSet}
				select={handleOnDateClick}
				eventClassNames={styles.event}
				resourceLabelClassNames={styles.resourceLabel}
				resourceAreaWidth={resourceAreaWidth}
				resourceAreaHeaderContent={resourcesLabel}
				eventContent={renderEvent}
				customButtons={customButtons}
				selectAllow={info => {
					return !info.resource?.extendedProps?.isSelectionDisabled;
				}}
				slotMinTime={settings?.slotMinTime ?? '00:00:00'}
				slotMaxTime={settings?.slotMaxTime ?? '24:00:00'}
				resourceLabelDidMount={data => {
					if (
						onResourceClick &&
						!data.resource?.extendedProps?.isSelectionDisabled
					) {
						data.el.style.cursor = 'pointer';
						data.el.addEventListener('click', () => {
							onResourceClick(data);
						});
					}
				}}
				eventOrder={eventOrder}
				eventOrderStrict={true}
			/>
			{isCalendarViewSettingsOpen && (
				<CalendarSettingsModal
					isOpen
					onSubmit={onSaveTimelineCalendarViewSettings}
					onClose={() => setIsCalendarViewSettingsOpen(false)}
					customSettingKey={customSettingKey}
					userId={userData.id}
					allowedFields={{
						showWeekends: false,
						highlightWorkingTime: false,
						defaultView: { enabled: false, options: [] },
						forceMinimumEventHeight: false,
						defaultEventLabel: false,
						defaultSlotDurationDay: {
							enabled: true,
							options: TIMELINE_CALENDAR_SLOT_DURATION_OPTIONS,
						},
						defaultSlotDurationWeek: {
							enabled: true,
							options: TIMELINE_CALENDAR_SLOT_DURATION_OPTIONS,
						},
						calendarViewTime: true,
					}}
				/>
			)}
		</div>
	);
};

export default TimelineCalendar;
